import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useLoaderData, useRevalidator, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { TContentGateElement, TContentGatePopupElement } from '@shared/dream-components';
import { ContentGateElement, ContentGatePopupElement } from '@shared/dream-components';

import { useContentGatePopup } from '~/hooks/useContentGatePopup';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';
import type { StaticPageLoaderData } from '~/services/static_page';
import { shouldClipAfterContentGate } from '~/utils/shouldClipAfterContentGate';

import { processContentGatePopupNode } from '../utils/processors';

import { serializeNode } from '../serializeNode';

interface Props {
  node: TContentGateElement;
  children: ReactNode | ReactNode[];
}

const ContentGate = ({ node, children }: Props) => {
  const [hasRevalidated, setHasRevalidated] = useState(false);
  const contentGateElementRef = useRef<HTMLDivElement>(null);
  const { subscription, publication } = useRouteLoaderData('root') as RootLoaderData;
  const loaderData = useLoaderData<StaticPageLoaderData | PostLoaderData>();
  const isContentClipped = loaderData.isContentClipped || false;
  const [searchParams] = useSearchParams();
  const tierIds = searchParams.getAll('tier_ids[]');
  const shouldClipContent = shouldClipAfterContentGate({
    contentGateNode: node,
    subscription,
    tierIds,
    cookiesHeader: typeof document !== 'undefined' ? document.cookie : '',
  });
  const revalidator = useRevalidator();
  const contentGatePopupNode = (node?.attrs?.popupContent as unknown as TContentGateElement)
    ?.content?.[0] as unknown as TContentGatePopupElement;
  const showContentGatePopup = node.attrs.showPopup === true;
  const { isOpen } = useContentGatePopup({
    useIntersectionLogic: true,
    isEnabled: isContentClipped && showContentGatePopup,
    contentGateElementRef,
  });

  // Revalidate loader data to fetch latest data
  // and unclip content
  useEffect(() => {
    if (!shouldClipContent && isContentClipped && !hasRevalidated) {
      revalidator.revalidate();
      setHasRevalidated(true);
    }
  }, [shouldClipContent, isContentClipped, revalidator.revalidate, hasRevalidated]);

  if (!isContentClipped) {
    return null;
  }

  const processedContentGatePopupNode = contentGatePopupNode
    ? processContentGatePopupNode(contentGatePopupNode, {
        publicationUrl: publication?.url,
      })
    : contentGatePopupNode;

  return (
    <div ref={contentGateElementRef}>
      <ContentGateElement element={node}>{children}</ContentGateElement>
      {showContentGatePopup && (
        <ContentGatePopupElement element={processedContentGatePopupNode} isLive isOpen={isOpen}>
          {serializeNode(processedContentGatePopupNode.content)}
        </ContentGatePopupElement>
      )}
    </div>
  );
};

export { ContentGate };
